import { getAttributionPayload } from "../../analytics/attribution";
import { apiClient, postRequest } from "../../services/apiService";
import type { BillingCycle, TierId } from "../../components/payments/checkoutIntent";
import type { EstimateCapture } from "../../services/lead-capture-service";
import { StartAnswers, toQuizPayload } from "./answers";

export interface TopBet {
  event: string;
  league: string | null;
  market: string | null;
  book: string;
  book_title: string;
  edge_percent: number;
  starts_at: string | null;
}

export interface Offer {
  trialDays: number;
}

/** The server profile: same answers, snake_case, plus the board filter other work reads. */
export interface QuizView {
  first_name: string | null;
  goal: string;
  habit: string | null;
  worry: string | null;
  state: string | null;
  books: string[];
  bankroll: string;
  time: string;
}

export async function fetchTopBets(books: string[], state: string): Promise<TopBet[]> {
  try {
    const { data } = await apiClient.get("/api/v1/public/top-bets", {
      params: { books: books.join(","), state: state || undefined },
    });
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

export async function fetchOffer(): Promise<Offer | null> {
  try {
    const { data } = await apiClient.get("/api/v1/public/quiz/offer");
    return { trialDays: Number(data?.trial_days) || 0 };
  } catch {
    return null;
  }
}

/** Whether this signed-in account would really get the trial. Null when unknown. */
export async function fetchTrialStatus(): Promise<{ eligible: boolean; days: number } | null> {
  try {
    const { data } = await apiClient.get("/api/v1/stripe/trial-status");
    return { eligible: !!data?.trial_eligible, days: Number(data?.trial_days) || 0 };
  } catch {
    return null;
  }
}

export async function fetchPlanByToken(token: string): Promise<QuizView | null> {
  try {
    return (await apiClient.get("/api/v1/public/quiz/plan", { params: { token } })).data;
  } catch {
    return null;
  }
}

export async function fetchMyQuiz(): Promise<QuizView | null> {
  try {
    return (await apiClient.get("/api/v1/quiz/me")).data;
  } catch {
    return null;
  }
}

/** Puts the answers on the account: state, bankroll and a starter filter if none are set. */
export async function saveQuizToAccount(answers: StartAnswers): Promise<QuizView | null> {
  try {
    return (await apiClient.post("/api/v1/quiz/me", toQuizPayload(answers))).data;
  } catch {
    return null;
  }
}

/** Starts the one abandon email an hour from now. Best effort. */
export function recordPaywallView(signedIn: boolean, planToken: string): void {
  const call = signedIn
    ? apiClient.post("/api/v1/quiz/me/paywall-view")
    : planToken
      ? apiClient.post("/api/v1/public/quiz/paywall-view", { token: planToken })
      : null;
  call?.catch(() => undefined);
}

export interface LeadResult {
  ok: boolean;
  planToken: string;
}

export async function captureQuizLead(
  answers: StartAnswers,
  email: string,
  estimate: EstimateCapture | undefined,
): Promise<LeadResult> {
  const result = await postRequest("/api/v1/public/email/capture", {
    email,
    state: answers.state || undefined,
    source: "quiz_start",
    ctaVariant: answers.entry || "direct",
    landingPath: window.location.pathname,
    referrer: document.referrer || undefined,
    estimate,
    quiz: toQuizPayload(answers),
  });
  return { ok: result?.ok === true, planToken: result?.planToken || "" };
}

export interface Consent {
  offerVersion: string;
  disclosure: string;
}

export interface CheckoutRequest {
  tier: TierId;
  cycle: BillingCycle;
  consent: Consent;
  expectTrial: boolean;
  entry: string;
}

export type CheckoutResult =
  | { kind: "redirect"; url: string }
  | { kind: "trial_unavailable" }
  | { kind: "failed" };

/** The body /create-checkout-session reads. Also used when checkout resumes after sign-in. */
export const checkoutBody = (request: CheckoutRequest) => ({
  tier: request.tier,
  billingCycle: request.cycle,
  origin: "start",
  entry: request.entry || undefined,
  consent: request.consent,
  expectTrial: request.expectTrial,
  attribution: getAttributionPayload() ?? undefined,
});

export async function startCheckout(request: CheckoutRequest): Promise<CheckoutResult> {
  try {
    const { data } = await apiClient.post(
      "/api/v1/stripe/create-checkout-session",
      checkoutBody(request),
    );
    return data?.checkoutSessionUrl
      ? { kind: "redirect", url: data.checkoutSessionUrl }
      : { kind: "failed" };
  } catch (error: any) {
    return error?.response?.data?.error === "TRIAL_UNAVAILABLE"
      ? { kind: "trial_unavailable" }
      : { kind: "failed" };
  }
}
